import { useEffect, useState } from "react";
import { RatingBadge } from "@/components/reviews/Stars";
import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import { vLabel } from "@/lib/version";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Info, Loader2, UserRound } from "lucide-react";
import { WarningBlock } from "@/components/market/WarningBlock";
import NotFound from "@/pages/NotFound";
import { api } from "@/lib/api";
import { nbBrand, useI18n } from "@/i18n/I18nContext";
import { PageHero } from "@/components/layout/PageHero";
import { NoveltyBanner } from "@/components/apps/NoveltyBanner";
import { BrandTag } from "@/components/apps/BrandTag";
import { MinisitePricing } from "@/components/market/Prepaid";
import { MegaScroller } from "@/components/apps/MegaScroller";
import { AntiCheatNote, CustomPlanCta } from "@/components/apps/PlanNotes";
import { AppIcon } from "@/components/apps/AppIcon";
import { PlatformBadge, PlatformDownload } from "@/components/apps/Platform";
import { AboutPanel } from "@/components/apps/AboutPanel";
import { MinisiteInfo } from "@/components/apps/MinisiteInfo";

const Meta = ({ label, value, testId }) => (
  <div className="rounded-xl border border-[rgba(245,197,66,0.18)] bg-black/40 p-4">
    <dt className="text-xs uppercase tracking-wider text-neutral-500">{label}</dt>
    <dd className="mt-1 font-display text-sm font-bold text-[var(--gold-bright)]" data-testid={testId}>{value}</dd>
  </div>
);

export default function AppDetail({ market = false }) {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [missing, setMissing] = useState(false);
  const [rating, setRating] = useState(null);
  const { t, fmtBytes, fmtDate, fmtDownloads } = useI18n();

  useEffect(() => {
    setApp(null);
    setMissing(false);
    api.get(market ? `/creators/apps/${id}` : `/public/apps/${id}`).then(({ data }) => setApp(data)).catch(() => setMissing(true));
  }, [id, market]);

  const v = app?.active_version;
  if (market && missing) return <NotFound gone />;
  return (
    <div data-testid="app-detail-page">
      <PageHero eyebrow={t("detail.eyebrow")}>
        <Link to={market ? "/developpeurs" : "/apps"} className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="back-to-catalogue-link">
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t(market ? "market.back" : "detail.back")}
        </Link>
      </PageHero>
      <section className="mx-auto max-w-4xl px-4 sm:px-8">
        {market && <WarningBlock className="mb-6" testId="app-detail-warning" />}
        {missing && (
          <div className="card-carbon rounded-2xl p-8 text-neutral-300" data-testid="app-not-found">{t("detail.notFound")}</div>
        )}
        {!missing && !app && <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />}
        {app && !app.developer && <div className="mb-4"><NoveltyBanner app={app} teaser testId="app-detail-novelty" /></div>}
        {app?.mega && <div className="mb-4 space-y-3"><MegaScroller mega={app.mega} testId="app-detail-mega" />{app.mega.field_count > 0 && <AntiCheatNote testId="app-detail-anticheat" />}</div>}
        {app?.category === "minisite" && <div className="mb-4"><MinisitePricing testId="app-detail-minisite-pricing" /></div>}
        {app && (
          <article className="card-carbon fade-up overflow-hidden rounded-3xl">
            <div className="metal-red flex flex-col gap-5 rounded-none border-x-0 border-t-0 p-6 sm:flex-row sm:items-center sm:p-8">
              <div className="flex min-w-0 shrink-0 flex-col items-start gap-3">
                <PlatformBadge app={app} testId="app-detail-platform" />
                <BrandTag app={app} testId="app-detail-brand" />
                <AppIcon app={app} size="h-24 w-24" px={96} testId="app-detail-icon" />
              </div>
              <div className="min-w-0">
                <h1 className="break-words font-display text-xl font-black uppercase tracking-wide text-white sm:text-2xl" data-testid="app-detail-name">
                  {app.name}
                </h1>
                {v && <p className="mt-1 font-display text-sm text-[var(--gold-bright)]">{t("detail.activeVersion", { v: vLabel(v) })}</p>}
                <p className="mt-1 text-sm text-neutral-300" data-testid="app-detail-downloads">{fmtDownloads(app.downloads)}</p>
                <div className="mt-1.5"><RatingBadge rating={rating || app.rating} testId="app-detail-rating" /></div>
                {app.developer && (
                  <Link to={`/developpeurs/${app.developer.slug}`} className="mt-2 inline-flex items-center gap-1.5 text-sm text-amber-200 hover:text-amber-100" data-testid="app-detail-developer">
                    <UserRound className="h-4 w-4" /> {t("market.by", { name: app.developer.brand })}
                  </Link>
                )}
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="whitespace-pre-line break-words [overflow-wrap:anywhere] text-sm leading-relaxed text-neutral-300 md:text-base" data-testid="app-detail-description">
                {nbBrand(app.description) || t("detail.noDescription")}
              </p>
              {app.category === "minisite" && <MinisiteInfo app={app} />}
              {v && (
                <dl className="mt-8 grid grid-cols-1 gap-3 min-[420px]:grid-cols-3">
                  <Meta label={t("apps.version")} value={vLabel(v)} testId="app-detail-version" />
                  <Meta label={t("apps.size")} value={fmtBytes(v.size)} testId="app-detail-size" />
                  <Meta label={t("apps.date")} value={fmtDate(v.created_at)} testId="app-detail-date" />
                </dl>
              )}
              <div className="mt-8">
                <PlatformDownload app={app} large autoStart testId="app-detail-download-btn" />
              </div>
              {v && <AboutPanel about={app.about} testId="app-detail-about" />}
              {v && (
                <p className="mt-6 flex gap-2 text-xs text-neutral-500">
                  <Info className="h-4 w-4 shrink-0" /> {t("detail.androidHint")}
                </p>
              )}
              <ReviewsSection appUrl={market ? `/creators/apps/${app.id}` : `/public/apps/${app.id}`} onRating={setRating} />
            </div>
          </article>
        )}
      </section>
    </div>
  );
}
